import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { useContent } from '@/content'
import { ROUTES } from '@/lib/paths'
import { Icon, Surface } from '@/components/ui'
import { NetworkLattice } from '@/features/lattice'
import styles from './DomainBoard.module.css'

/**
 * The three domains, which are how the whole site is organised.
 *
 * This is the home page's spine: a visitor should be able to see the shape
 * of the practice here and jump straight into the evidence for whichever
 * part is theirs, without reading past the other two.
 */
export const DomainBoard = () => {
  const { domains, projects } = useContent()

  const countFor = (id: string) =>
    projects.filter((p) => p.classifications.some((c) => c.domain === id)).length

  return (
    <Surface pad={30} radius="shell" className={styles.board}>
      <NetworkLattice />

      <div className={styles.head}>
        <h2 className="t-24">Three domains</h2>
        <p className={`t-14 ${styles.lede}`}>
          Design, the application, and the infrastructure underneath it. Most
          projects here touch more than one.
        </p>
      </div>

      <ul className={styles.grid}>
        {domains.map((domain, index) => (
          <motion.li
            key={domain.id}
            initial={{ opacity: 0, transform: 'translateY(18px)' }}
            whileInView={{ opacity: 1, transform: 'translateY(0px)' }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{
              duration: 0.5,
              delay: index * 0.08,
              ease: [0.23, 1, 0.32, 1],
            }}
          >
            <Surface
              as={Link}
              to={`${ROUTES.projects}?domain=${domain.id}`}
              interactive
              radius="card"
              className={styles.card}
            >
              <span className={styles.icon} aria-hidden="true">
                <Icon name={domain.icon} size={24} />
              </span>

              <h3 className={`t-20 ${styles.name}`}>{domain.name}</h3>
              <p className={`t-14 ${styles.description}`}>{domain.description}</p>

              <ul className={styles.tags}>
                {domain.categories.map((category) => (
                  <li key={category.id} className={styles.tag}>
                    {category.name}
                  </li>
                ))}
              </ul>

              <span className={styles.footerRow}>
                <span className={`t-12 ${styles.count}`}>
                  {countFor(domain.id)} projects
                </span>
                <span className={styles.go}>
                  View
                  <Icon name="arrowUpRight" size={12} className={styles.goIcon} />
                </span>
              </span>
            </Surface>
          </motion.li>
        ))}
      </ul>
    </Surface>
  )
}
